/**
 * PKCE (RFC 7636) helpers — generate verifier, challenge, and a state nonce.
 *
 * - verifier: 64-char base64url-encoded random (48 bytes)
 * - challenge: base64url(SHA-256(verifier))
 * - state: 43-char base64url-encoded random (32 bytes)
 *
 * No deps — pure Web Crypto.
 */
export interface PkceBundle {
  verifier: string
  challenge: string
  state: string
}

const CHALLENGE_METHOD = 'S256' as const
export const PKCE_CHALLENGE_METHOD = CHALLENGE_METHOD

/** Generate a complete PKCE bundle. */
export async function generatePkce(): Promise<PkceBundle> {
  const verifier = randomBase64Url(48)
  const state = randomBase64Url(32)
  const challenge = await sha256Base64Url(verifier)
  return { verifier, challenge, state }
}

/** SHA-256 over a string, returned as base64url. Exposed for tests. */
export async function sha256Base64Url(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input)
  const buf = await crypto.subtle.digest('SHA-256', enc)
  return base64Url(new Uint8Array(buf))
}

/** Cryptographically random base64url string of `byteLen` bytes of entropy. */
export function randomBase64Url(byteLen: number): string {
  const bytes = new Uint8Array(byteLen)
  crypto.getRandomValues(bytes)
  return base64Url(bytes)
}

/** Base64url-encode a Uint8Array. No padding, URL-safe alphabet. */
export function base64Url(bytes: Uint8Array): string {
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
